import {
  BookOpenText,
  CircleCheck,
  CircleDot,
  FileText,
  Hash,
  Link,
  List,
  MessageSquare,
  Mic,
  PenLine,
  SquareCheckBig,
  type LucideIcon,
} from 'lucide-react';
import type { StatusTone } from 'components/ui/status-badge';

/** Label keys are product-specific: MULTIPLE_CHOICE = "single choice", MULTIPLE_RESPONSE = "multiple choice". */
export interface QuestionTypeMeta {
  icon: LucideIcon;
  /** i18n key for the human label. */
  labelKey: string;
}

export const QUESTION_TYPE_META: Record<string, QuestionTypeMeta> = {
  MULTIPLE_CHOICE: { icon: CircleDot, labelKey: 'single_choice' },
  MULTIPLE_RESPONSE: { icon: SquareCheckBig, labelKey: 'multiple_choice' },
  TRUE_FALSE: { icon: CircleCheck, labelKey: 'true_false' },
  SHORT_ANSWER: { icon: PenLine, labelKey: 'short_answer' },
  MATCHING: { icon: Link, labelKey: 'matching' },
  FILL_IN_BLANKS: { icon: FileText, labelKey: 'fill_in_blanks' },
  OPEN_ENDED: { icon: MessageSquare, labelKey: 'open_ended' },
  REORDER: { icon: List, labelKey: 'reorder' },
  PASSAGE: { icon: BookOpenText, labelKey: 'passage' },
  // SAT grid-in; only offered inside Digital SAT Math sections, so it is not in QUESTION_TYPE_ORDER.
  SPR: { icon: Hash, labelKey: 'exam.spr.type' },
  SPEAKING: { icon: Mic, labelKey: 'exam.speaking.type' },
};

/** Canonical ordering for the type filter dropdown. */
export const QUESTION_TYPE_ORDER: string[] = [
  'MULTIPLE_CHOICE',
  'MULTIPLE_RESPONSE',
  'TRUE_FALSE',
  'SHORT_ANSWER',
  'MATCHING',
  'FILL_IN_BLANKS',
  'OPEN_ENDED',
  'REORDER',
  'PASSAGE',
];

export const DEFAULT_TYPE_META: QuestionTypeMeta = {
  icon: FileText,
  labelKey: '',
};

// The five basic types are free on every plan; the rest are Growth+ (gated by
// FEATURES.ADVANCED_QUESTION_TYPES). Mirrors apps/api/src/common/question-types.ts.
export const BASIC_QUESTION_TYPES = new Set<string>([
  'MULTIPLE_CHOICE',
  'MULTIPLE_RESPONSE',
  'TRUE_FALSE',
  'SHORT_ANSWER',
  'MATCHING',
]);

export const isAdvancedQuestionType = (type: string): boolean => !BASIC_QUESTION_TYPES.has(type);

export const DIFFICULTIES = ['easy', 'medium', 'hard'] as const;
export type Difficulty = (typeof DIFFICULTIES)[number];

/** Difficulty reads like a score band: easy green, medium amber, hard red. */
export const DIFFICULTY_TONE: Record<Difficulty, StatusTone> = { easy: 'success', medium: 'warning', hard: 'error' };
